import { useEffect } from 'react';
import { usePageSEO } from '@/hooks/usePageSEO';
import FadeInSection from '@/components/base/FadeInSection';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Hero from './components/Hero';
import ConnaitreQuartier from './components/ConnaitreQuartier';
import ProblemesTypiques from './components/ProblemesTypiques';
import ServicesQuartier from './components/ServicesQuartier';
import PrixTypiques from './components/PrixTypiques';
import ProjetsRecents from './components/ProjetsRecents';
import TemoignageLocal from './components/TemoignageLocal';
import ZonesProches from './components/ZonesProches';
import CTAFinal from './components/CTAFinal';

function setMeta(name: string, content: string) {
  let meta = document.querySelector(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function removeOldLdJson() {
  const existing = document.querySelectorAll('script[type="application/ld+json"]');
  existing.forEach((el) => {
    if (el.id === 'saintromuald-ld') el.remove();
  });
}

const breadcrumbItems = [
  { label: 'Accueil', href: '/' },
  { label: 'Secteurs', href: null },
  { label: 'Saint-Romuald', href: '/secteurs/saint-romuald' },
];

export default function SaintRomualdPage() {
  usePageSEO({
    title: "Couvreur à Saint-Romuald | Lévis | Condos & Multi-logements | RBQ",
    description: "Couvreur à Saint-Romuald, Lévis. Toiture condos, copropriétés et multi-logements près de la rue Saint-Jean-Baptiste. Urgence 7j/7. Soumission gratuite sous 2h. Couvreurs RBQ. 418-476-9950.",
    urlPath: "/secteurs/saint-romuald",
    keywords: "couvreur Saint-Romuald, toiture Saint-Romuald, réfection Saint-Romuald, bardeau Lévis, condo, copropriété, multi-logement, rue Saint-Jean-Baptiste, soumission",
    ogTitle: "Couvreur à Saint-Romuald — Condos et copropriétés",
    ogDescription: "Réfection et réparation de toitures pour condos et multi-logements à Saint-Romuald. Plus de 80 projets réalisés, intervention sous 24h en urgence.",
    ogImage: 'https://couvreur-levis.com/og/og-saintromuald.jpg',
    ogImageAlt: 'Couvreur à Saint-Romuald, Lévis - Toiture condos et copropriétés',
    geoPosition: "46.8156;-71.1986",
    geoPlacename: "Saint-Romuald, Lévis, QC",
    geoRegion: "CA-QC",
  });

  useEffect(() => {
    document.title = "Couvreur à Saint-Romuald | Lévis | Condos & Multi-logements | RBQ";
    setMeta('description', "Couvreur à Saint-Romuald, Lévis. Toiture condos, copropriétés et multi-logements près de la rue Saint-Jean-Baptiste. Urgence 7j/7. Soumission gratuite sous 2h. Couvreurs RBQ. 418-476-9950.");
    setMeta('keywords', 'couvreur Saint-Romuald, toiture Saint-Romuald, réfection Saint-Romuald, bardeau Lévis, condo, copropriété, multi-logement, rue Saint-Jean-Baptiste, soumission');
    setMeta('geo.position', '46.8156;-71.1986');
    setMeta('geo.placename', 'Saint-Romuald, Lévis, QC');
    setMeta('geo.region', 'CA-QC');

    removeOldLdJson();

    const ld = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'LocalBusiness',
          name: 'Couvreur-Lévis.com — Saint-Romuald',
          url: 'https://couvreur-levis.com/secteurs/saint-romuald',
          telephone: '+1-418-476-9950',
          email: 'contact@couvreur-levis.com',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Saint-Romuald',
            addressRegion: 'QC',
            addressCountry: 'CA',
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: 46.7196,
            longitude: -71.2782,
          },
          openingHoursSpecification: [
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
              opens: '07:00',
              closes: '18:00',
            },
          ],
          priceRange: '$$',
          areaServed: {
            '@type': 'Place',
            name: 'Saint-Romuald, Lévis',
          },
          description:
            "Couvreur à Saint-Romuald, Lévis. Toiture condos, copropriétés et multi-logements près de la rue Saint-Jean-Baptiste. Urgence 7j/7. Soumission gratuite sous 2h. Couvreurs RBQ. 418-476-9950.",
          image: 'https://couvreur-levis.com/og/og-saintromuald.jpg',
          hasMap: 'https://www.google.com/maps?q=Saint-Romuald,L%C3%A9vis,QC',
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: breadcrumbItems.map((item, idx) => ({
            '@type': 'ListItem',
            position: idx + 1,
            name: item.label,
            item: item.href ? `https://couvreur-levis.com${item.href}` : undefined,
          })),
        },
        {
          '@type': 'Organization',
          name: 'Couvreur-Lévis.com',
          url: 'https://couvreur-levis.com',
          logo: 'https://couvreur-levis.com/og-default.jpg',
          description: 'Plateforme de mise en relation avec des couvreurs certifiés à Lévis et environs.',
          telephone: '+1-418-476-9950',
          email: 'contact@couvreur-levis.com',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Lévis',
            addressRegion: 'QC',
            addressCountry: 'CA',
          },
        },
        {
          '@type': 'Service',
          serviceType: 'Services de toiture',
          provider: { '@type': 'LocalBusiness', '@id': 'https://couvreur-levis.com/#business' },
          areaServed: {
            '@type': 'Place',
            name: 'Saint-Romuald',
            containedInPlace: { '@type': 'City', name: 'Lévis' },
          },
          description: "Couvreurs partenaires à Saint-Romuald, Lévis. Toiture, réfection, réparation, urgence. Soumission gratuite en 2h.",
        },
      ],
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'saintromuald-ld';
    script.textContent = JSON.stringify(ld);
    document.head.appendChild(script);

    return () => {
      removeOldLdJson();
    };
  }, []);

  return (
    <div className="font-inter min-h-screen bg-white">
      <Header />
      <main>
        <Hero />
        <FadeInSection delay={100}><ConnaitreQuartier /></FadeInSection>
        <FadeInSection delay={200}><ProblemesTypiques /></FadeInSection>
        <FadeInSection delay={300}><ServicesQuartier /></FadeInSection>
        <FadeInSection delay={400}><PrixTypiques /></FadeInSection>
        <FadeInSection delay={500}><ProjetsRecents /></FadeInSection>
        <FadeInSection delay={600}><TemoignageLocal /></FadeInSection>
        <FadeInSection delay={700}><ZonesProches /></FadeInSection>
        <FadeInSection delay={800}><CTAFinal /></FadeInSection>
      </main>
      <Footer />
    </div>
  );
}